import { MessageSquareWarning } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Row } from '@tanstack/react-table';

import {
  BasicTableSettings,
  RefreshableTableSettings,
  createPersistedStore,
  refreshableSettings,
} from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { Datatable } from '@@/datatables/Datatable';
import { TableSettingsMenu } from '@@/datatables/TableSettingsMenu';
import { TableSettingsMenuAutoRefresh } from '@@/datatables/TableSettingsMenuAutoRefresh';
import {
  StatusSummaryBar,
  StatusSegment,
} from '@@/StatusSummaryBar/StatusSummaryBar';

import { useCombinedAlertSettings } from '../AlertSettingsDatatable/useAlertSettings';
import { AlertSettingsRow } from '../AlertSettingsDatatable/types';
import { AlertRuleRow } from '../../types';
import {
  AlertRuleCategory,
  AlertRuleCategoryFilter,
  alertRuleCategoryOrder,
  getAlertRuleCategoryLabel,
  resolveAlertRuleCategory,
} from '../alertRuleCategory';

import { createColumns } from './columns';
import { useAlertRules, useUpdateAlertRule } from './useAlertRules';
import { transformArrayToAlertRuleRow } from './utils';

interface TableSettings extends BasicTableSettings, RefreshableTableSettings {}

const storageKey = 'portainer.alert-rules';
const settingsStore = createPersistedStore<TableSettings>(
  storageKey,
  'name',
  (set) => ({
    ...refreshableSettings(set, 10_000),
  })
);

const categoryOrder = Object.fromEntries(
  alertRuleCategoryOrder.map((c, i) => [c, i])
) as Record<AlertRuleCategory, number>;

export function AlertRulesDatatable() {
  const [togglingId, setTogglingId] = useState<number | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] =
    useState<AlertRuleCategoryFilter | null>(null);
  const tableState = useTableState(settingsStore, storageKey);
  const alertRulesQuery = useAlertRules({
    refetchInterval: tableState.autoRefreshRateMS
      ? tableState.autoRefreshRateMS
      : undefined,
    select: transformArrayToAlertRuleRow,
  });

  const updateAlertRule = useUpdateAlertRule();

  function handleToggle(alertRule: AlertRuleRow, enabled: boolean) {
    setTogglingId(alertRule.id);
    updateAlertRule.mutate(
      { ...alertRule, enabled },
      {
        onSuccess: () => {
          setTogglingId(undefined);
        },
        onError: () => {
          setTogglingId(undefined);
        },
      }
    );
  }

  const alertSettings = useCombinedAlertSettings({
    select: (data: AlertSettingsRow[]) =>
      data.some((setting: AlertSettingsRow) => setting.enabled),
  });

  const columns = createColumns(handleToggle, togglingId, alertSettings.data);
  const alertRules = useMemo(
    () => alertRulesQuery.data || [],
    [alertRulesQuery.data]
  );

  const rulesByCategory = useMemo(
    () =>
      alertRules.reduce<Record<AlertRuleCategory, AlertRuleRow[]>>(
        (acc, rule) => {
          const category = resolveAlertRuleCategory(rule);
          acc[category].push(rule);
          return acc;
        },
        { portainer: [], security: [], environment: [] }
      ),
    [alertRules]
  );

  const categorySegments: StatusSegment<AlertRuleCategoryFilter>[] = useMemo(
    () =>
      alertRuleCategoryOrder.map((category) => ({
        key: category,
        label: getAlertRuleCategoryLabel(category),
        count: rulesByCategory[category].length,
        color: 'blue' as const,
      })),
    [rulesByCategory]
  );

  const visibleRules = useMemo(() => {
    const filtered = selectedCategory
      ? alertRules.filter(
          (rule) => resolveAlertRuleCategory(rule) === selectedCategory
        )
      : alertRules;
    return [...filtered].sort(
      (a, b) =>
        categoryOrder[resolveAlertRuleCategory(a)] -
        categoryOrder[resolveAlertRuleCategory(b)]
    );
  }, [alertRules, selectedCategory]);

  function getGroupKey(row: Row<AlertRuleRow>) {
    return getAlertRuleCategoryLabel(resolveAlertRuleCategory(row.original));
  }

  return (
    <>
      <div className="row">
        <div className="col-sm-12">
          <StatusSummaryBar
            total={alertRules.length}
            segments={categorySegments}
            value={selectedCategory}
            onChange={(value) => setSelectedCategory(value)}
            radioGroupName="alert-rules-category"
            ariaLabel="Filter alert rules by category"
            data-cy="alert-rules-category"
          />
        </div>
      </div>

      <Datatable
        dataset={visibleRules}
        columns={columns}
        isLoading={alertRulesQuery.isLoading}
        settingsManager={tableState}
        emptyContentLabel="No alert rules available yet"
        title="Alert Rules"
        titleIcon={MessageSquareWarning}
        disableSelect
        getGroupKey={getGroupKey}
        groupHeaderClassName="!bg-gray-3 !py-2 text-sm font-bold uppercase tracking-wider !border-t-2 !border-gray-5 th-highcontrast:!bg-black th-highcontrast:text-white th-dark:!bg-gray-iron-9 th-dark:text-white th-dark:!border-gray-iron-7"
        renderTableSettings={() => (
          <TableSettingsMenu>
            <TableSettingsMenuAutoRefresh
              onChange={tableState.setAutoRefreshRate}
              value={tableState.autoRefreshRateMS}
            />
          </TableSettingsMenu>
        )}
        data-cy="alert-rules-datatable"
      />
    </>
  );
}
